import { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid';
import Container from '@mui/material/Container';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
} from '@mui/material';
import Swal from 'sweetalert2';
import request from '../../services/Request';
import '../user/form.css';
import { Modal } from '../../components/BootstrapToMUI';
import { useTheme } from '@mui/material/styles';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

export default function DisableMachineModal(props) {
  const theme = useTheme();
  const { data, setMachines, setRowData, setReflesh, reflesh, ...rest } = props;
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('disable');

  // Set default action based on machine's current disable status
  useEffect(() => {
    if (props.show && data && data.length > 0) {
      // If machine is currently enabled (disable: false), default action is 'enable'
      // If machine is currently disabled (disable: true), default action is 'disable'
      const isDisabled = data[0].disable;
      setAction(isDisabled === false ? 'enable' : 'disable');
    }
  }, [props.show, data]);

  const handleSubmit = async () => {
    const machineIds = data.map((row) => row._id);
    const newDisableStatus = action === 'disable';

    setLoading(true);

    await request({
      method: 'post',
      url: `${process.env.REACT_APP_SERVER_API}/machines/toggleStatus`,
      data: { machineIds, disable: newDisableStatus },
    })
      .then(() => {
        setLoading(false);

        // Update local state immediately
        if (setMachines) {
          setMachines((prevMachines) =>
            prevMachines.map((machine) =>
              machineIds.includes(machine._id)
                ? { ...machine, disable: newDisableStatus }
                : machine
            )
          );
        }

        if (setRowData) {
          setRowData((prevRowData) =>
            prevRowData.map((machine) =>
              machineIds.includes(machine._id)
                ? { ...machine, disable: newDisableStatus }
                : machine
            )
          );
        }

        Swal.fire({
          title: 'Success!',
          text: `${machineIds.length} machine(s) have been ${action}d.`,
          icon: 'success',
        }).then(() => {
          if (setReflesh) {
            setReflesh(!reflesh);
          }
          props.onHide();
        });
      })
      .catch((err) => {
        setLoading(false);
        Swal.fire({
          title: 'Error!',
          text: err?.response?.data?.message || `Failed to ${action} machines.`,
          icon: 'error',
        });
      });
  };

  return (
    <Modal className="modal-w" {...rest} centered size="md">
      <Modal.Header closeButton>
        <Modal.Title
          id="contained-modal-title-vcenter"
          sx={{
            fontSize: '1.25rem',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {action === 'disable' ? (
            <StopRoundedIcon color="error" />
          ) : (
            <CheckCircleIcon color="success" />
          )}
          Toggle Machine Status
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="modal-body">
        <Container component="main" maxWidth="sm">
          <CssBaseline />
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600 }}>
                    Select Action
                  </FormLabel>
                  <RadioGroup
                    row
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <FormControlLabel
                      value="disable"
                      control={<Radio color="error" />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StopRoundedIcon fontSize="small" color="error" />
                          <Typography>Disable</Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="enable"
                      control={<Radio color="success" />}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircleIcon fontSize="small" color="success" />
                          <Typography>Enable</Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Alert severity={action === 'disable' ? 'warning' : 'info'} sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {action === 'disable'
                      ? `Warning: You are about to disable ${data.length} machine(s)`
                      : `You are about to enable ${data.length} machine(s)`}
                  </Typography>
                </Alert>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Selected Machines:
                </Typography>
                <List
                  sx={{
                    maxHeight: 300,
                    overflow: 'auto',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  {data.map((machine, index) => (
                    <ListItem
                      key={machine._id}
                      sx={{
                        borderBottom:
                          index < data.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                      }}
                    >
                      <ListItemText
                        primary={machine.name}
                        secondary={machine.description || 'No description'}
                        primaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  This action will {action} all selected machines. Are you sure you want to
                  continue?
                </Typography>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
              <Button
                variant="outlined"
                onClick={props.onHide}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color={action === 'disable' ? 'error' : 'success'}
                startIcon={action === 'disable' ? <StopRoundedIcon /> : <CheckCircleIcon />}
                onClick={handleSubmit}
                disabled={loading}
                sx={{ textTransform: 'none' }}
              >
                {loading
                  ? `${action === 'disable' ? 'Disabling' : 'Enabling'}...`
                  : `${action === 'disable' ? 'Disable' : 'Enable'} Machines`}
              </Button>
            </Box>
          </Box>
        </Container>
      </Modal.Body>
    </Modal>
  );
}
